
import jsPDF from 'jspdf';

interface SalaryExportData {
  // Professional Data
  grossIncome: number;
  numberOfPayments: string;
  contractType: string;
  professionalCategory: string;
  geographicMobility: boolean;
  movedResidence: boolean;

  // Personal Data
  country: string;
  age: number;
  familyStatus: string;
  hasDisability: boolean;
  disabilityDegree: number;
  region: string;
  dependents: number;
  churchMember: boolean;
  pensionContributions: number;
  charitableDonations: number;

  // Results
  grossAnnual: number;
  grossMonthly: number;
  taxDeduction: number;
  socialSecurityDeduction: number;
  retirementDeduction: number;
  healthInsuranceDeduction: number;
  otherDeductions: number;
  allowances: number;
  netAnnual: number;
  netMonthly: number;
}

interface TranslationFunction {
  (key: string): string;
}

export const generateSalaryPDF = (
  data: SalaryExportData,
  currency: string,
  t: TranslationFunction
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPosition = 30;

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(currency === "USD" ? "en-US" : "de-DE", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Helper function to get frequency text
  const getFrequencyText = (frequency: string) => {
    switch (frequency) {
      case "12": return t("tools.salary.paymentOptions.monthly");
      case "14": return t("tools.salary.paymentOptions.withExtras");
      default: return frequency;
    }
  };

  // Helper function to get contract type text
  const getContractTypeText = (contractType: string) => {
    switch (contractType) {
      case "permanent": return t("tools.salary.permanent");
      case "temporary": return t("tools.salary.temporary");
      case "training": return t("tools.salary.training");
      default: return contractType;
    }
  };

  // Helper function to get family status text
  const getFamilyStatusText = (familyStatus: string) => {
    switch (familyStatus) {
      case "single": return t("tools.salary.single");
      case "married": return t("tools.salary.married");
      case "head": return t("tools.salary.head");
      default: return familyStatus;
    }
  };

  // Helper function to get country text
  const getCountryText = (country: string) => {
    switch (country) {
      case "Spain": return t("tools.salary.spain");
      case "USA": return t("tools.salary.usa");
      case "France": return t("tools.salary.france");
      case "UK": return t("tools.salary.uk");
      case "Italy": return t("tools.salary.italy");
      case "Switzerland": return t("tools.salary.switzerland");
      default: return country;
    }
  };

  // Title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(t("tools.salary.title"), pageWidth / 2, yPosition, { align: "center" });
  yPosition += 20;

  // Professional Data Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(t("tools.salary.professionalData"), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  
  const professionalData = [
    [t("tools.salary.grossIncome"), formatCurrency(data.grossIncome)],
    [t("tools.salary.numberOfPayments"), getFrequencyText(data.numberOfPayments)],
    [t("tools.salary.contractType"), getContractTypeText(data.contractType)],
    [t("tools.salary.professionalCategory"), data.professionalCategory],
  ];

  if (data.geographicMobility) {
    professionalData.push([t("tools.salary.geographicMobility"), t("common.yes")]);
    if (data.movedResidence) {
      professionalData.push([t("tools.salary.movedResidence"), t("common.yes")]);
    }
  }

  professionalData.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, margin, yPosition);
    yPosition += 8;
  });

  yPosition += 10;

  // Personal Data Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(t("tools.salary.personalData"), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");

  const personalData = [
    [t("tools.salary.country"), getCountryText(data.country)],
    [t("tools.salary.age"), data.age.toString()],
    [t("tools.salary.familyStatus"), getFamilyStatusText(data.familyStatus)],
    [t("tools.salary.dependents"), data.dependents.toString()],
    [t("tools.salary.region"), data.region],
  ];

  if (data.hasDisability) {
    personalData.push([t("tools.salary.hasDisability"), t("common.yes")]);
    if (data.disabilityDegree > 0) {
      personalData.push([t("tools.salary.disabilityDegree"), `${data.disabilityDegree}%`]);
    }
  }

  if (data.churchMember) {
    personalData.push([t("tools.salary.churchMember"), t("common.yes")]);
  }

  personalData.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, margin, yPosition);
    yPosition += 8;
  });

  yPosition += 10;

  // Additional Deductions Section
  if (data.pensionContributions > 0 || data.charitableDonations > 0) {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(t("tools.salary.additionalDeductions"), margin, yPosition);
    yPosition += 15;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    if (data.pensionContributions > 0) {
      doc.text(`${t("tools.salary.pensionContributions")}: ${formatCurrency(data.pensionContributions)}`, margin, yPosition);
      yPosition += 8;
    }

    if (data.charitableDonations > 0) {
      doc.text(`${t("tools.salary.charitableDonations")}: ${formatCurrency(data.charitableDonations)}`, margin, yPosition);
      yPosition += 8;
    }

    yPosition += 10;
  }

  // Results Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(t("common.results"), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");

  const resultData = [
    [t("tools.salary.grossAnnual"), formatCurrency(data.grossAnnual)],
    [t("tools.salary.grossMonthly"), formatCurrency(data.grossMonthly)],
    [t("tools.salary.taxDeduction"), `- ${formatCurrency(data.taxDeduction)}`],
    [t("tools.salary.socialSecurity"), `- ${formatCurrency(data.socialSecurityDeduction)}`],
  ];

  if (data.retirementDeduction > 0) {
    resultData.push([t("tools.salary.retirement"), formatCurrency(data.retirementDeduction)]);
  }

  if (data.otherDeductions > 0) {
    resultData.push([t("tools.salary.deductions"), formatCurrency(data.otherDeductions)]);
  }

  resultData.push(
    [t("tools.salary.netAnnual"), formatCurrency(data.netAnnual)],
    [t("tools.salary.netMonthly"), formatCurrency(data.netMonthly)]
  );

  resultData.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, margin, yPosition);
    yPosition += 8;
  });

  // Footer
  const currentDate = new Date().toLocaleDateString();
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.text(`Generated on ${currentDate} by Finanzly`, pageWidth / 2, 285, { align: "center" });

  return doc;
};
